import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Edit3, Trash2, FileText, TrendingUp, Clock } from 'lucide-react';
import { Essay } from '../../types';
import { Card } from '../shared/Card';
import { Badge } from '../shared/Badge';

interface EssayCardProps {
  essay: Essay;
  onEdit: (essay: Essay) => void;
  onDelete: (id: string) => void;
}

export const EssayCard: React.FC<EssayCardProps> = ({ essay, onEdit, onDelete }) => {
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'final': return 'success';
      case 'revised': return 'secondary';
      case 'in-review': return 'warning';
      case 'draft': return 'accent';
      default: return 'secondary';
    }
  };

  const getTypeLabel = (type: string) => {
    return type.split('-').map(word =>
      word.charAt(0).toUpperCase() + word.slice(1)
    ).join(' ');
  };

  const formatDate = (date: Date) => {
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  return (
    <Card hover className="group relative">
      <div className="flex justify-between items-start mb-4">
        <div className="flex-1">
          <h3 className="text-lg font-semibold text-gray-900 mb-2 line-clamp-1">
            {essay.title || 'Untitled Essay'}
          </h3>
          <div className="flex items-center space-x-2 mb-3">
            <Badge variant={getStatusColor(essay.status)} size="sm">
              {essay.status.replace('-', ' ')}
            </Badge>
            <Badge variant="accent" size="sm">
              {getTypeLabel(essay.type)}
            </Badge>
          </div>
        </div>

        <div className="flex items-center space-x-2 opacity-0 group-hover:opacity-100 transition-opacity">
          <button
            onClick={() => onEdit(essay)}
            className="p-2 text-gray-400 hover:text-primary-600 transition-colors"
          >
            <Edit3 size={16} />
          </button>
          <button
            onClick={() => onDelete(essay.id)}
            className="p-2 text-gray-400 hover:text-red-600 transition-colors"
          >
            <Trash2 size={16} />
          </button>
        </div>
      </div>

      {/* Essay Preview */}
      <div className="mb-4">
        <p className="text-gray-600 text-sm line-clamp-3">
          {essay.content || 'No content yet. Click to start writing...'}
        </p>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
        <div className="flex items-center text-gray-500">
          <FileText size={14} className="mr-2" />
          <span>{essay.wordCount} words</span>
        </div>
        <div className="flex items-center text-gray-500">
          <Calendar size={14} className="mr-2" />
          <span>{formatDate(essay.lastModified)}</span>
        </div>
      </div>

      {/* AI Analysis Score */}
      {essay.aiAnalysis && (
        <div className="flex items-center justify-between pt-3 border-t border-gray-100">
          <div className="flex items-center text-sm text-gray-600">
            <TrendingUp size={14} className="mr-2 text-primary-500" />
            <span>AI Score: {essay.aiAnalysis.overallScore}/100</span>
          </div>
          <div className="flex items-center text-xs text-gray-500">
            <Clock size={12} className="mr-1" />
            <span>Last analyzed</span>
          </div>
        </div>
      )}

      {/* Feedback Count */}
      {essay.feedback.length > 0 && (
        <div className="mt-2 text-xs text-primary-600">
          {essay.feedback.length} AI suggestion{essay.feedback.length !== 1 ? 's' : ''} available
        </div>
      )}

      {/* Click to Edit Overlay */}
      <motion.div
        className="absolute inset-0 bg-primary-500 bg-opacity-0 flex items-center justify-center opacity-0 group-hover:bg-opacity-5 group-hover:opacity-100 transition-all duration-200 rounded-xl cursor-pointer"
        onClick={() => onEdit(essay)}
      >
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          whileHover={{ scale: 1, opacity: 1 }}
          className="bg-white px-4 py-2 rounded-lg shadow-lg border border-gray-200"
        >
          <span className="text-sm font-medium text-gray-700">Click to edit</span>
        </motion.div>
      </motion.div>
    </Card>
  );
};